import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus } from "lucide-react";

export default function Tenders() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["tenders"], queryFn: () => api.get("/tenders").then((r) => r.data) });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", department: "", tender_no: "", quantity_litres: 100000, est_value: 9500000, submission_date: "", opening_date: "", validity_date: "", emd_amount: 100000, notes: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value });

  const create = useMutation({
    mutationFn: (p) => api.post("/tenders", p),
    onSuccess: () => { toast.success("Tender added"); setOpen(false); qc.invalidateQueries({ queryKey: ["tenders"] }); },
  });
  const patch = useMutation({
    mutationFn: ({ id, patch }) => api.patch(`/tenders/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenders"] }),
  });

  const STATUSES = ["identified", "preparing", "submitted", "won", "lost", "expired"];

  return (
    <div className="space-y-4" data-testid="tenders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Tenders & Govt Contracts</h1>
          <p className="text-sm text-muted-foreground">{data?.items?.length || 0} tenders tracked</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="btn-new-tender"><Plus size={14} className="mr-1" /> Add tender</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>New tender</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Title</Label><Input value={form.title} onChange={set("title")} data-testid="tn-title" /></div>
              <div><Label>Department</Label><Input value={form.department} onChange={set("department")} data-testid="tn-dept" /></div>
              <div><Label>Tender no</Label><Input value={form.tender_no} onChange={set("tender_no")} /></div>
              <div><Label>Quantity (L)</Label><Input type="number" value={form.quantity_litres} onChange={set("quantity_litres")} /></div>
              <div><Label>Est. value ₹</Label><Input type="number" value={form.est_value} onChange={set("est_value")} /></div>
              <div><Label>Submission date</Label><Input type="date" value={form.submission_date} onChange={set("submission_date")} /></div>
              <div><Label>Opening date</Label><Input type="date" value={form.opening_date} onChange={set("opening_date")} /></div>
              <div><Label>Validity</Label><Input type="date" value={form.validity_date} onChange={set("validity_date")} /></div>
              <div><Label>EMD amount ₹</Label><Input type="number" value={form.emd_amount} onChange={set("emd_amount")} /></div>
            </div>
            <DialogFooter><Button onClick={() => create.mutate(form)} disabled={!form.title || !form.department} data-testid="btn-save-tender">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="data-table w-full">
            <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr><th className="px-4 py-3">Code</th><th className="px-4 py-3">Title</th><th className="px-4 py-3">Department</th><th className="px-4 py-3 text-right">Qty (L)</th><th className="px-4 py-3 text-right">Value</th><th className="px-4 py-3">Submission</th><th className="px-4 py-3">Status</th></tr>
            </thead>
            <tbody>
              {(data?.items || []).map((t) => (
                <tr key={t.id} className="border-b border-border/40 text-sm">
                  <td className="px-4 py-3 font-mono font-semibold">{t.code}</td>
                  <td className="px-4 py-3">{t.title}</td>
                  <td className="px-4 py-3 text-xs">{t.department}</td>
                  <td className="px-4 py-3 text-right font-mono">{t.quantity_litres?.toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 text-right font-mono">₹{t.est_value?.toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3 text-xs">{t.submission_date || "—"}</td>
                  <td className="px-4 py-3">
                    <select value={t.status} onChange={(e) => patch.mutate({ id: t.id, patch: { status: e.target.value } })} className="rounded border border-border bg-background px-2 py-0.5 text-xs">
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
              {(!data?.items || data.items.length === 0) && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">No tenders yet.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
